import { Controller } from "@hotwired/stimulus"

// Ticks the wallboard's clocks once a second between the dashboard's own
// refreshes. Times come from data-at (epoch seconds), so each refresh simply
// replaces them. Clocks count from the server's render time (the "updated"
// target's data-at) plus the time elapsed here, so a wall screen whose own
// clock is wrong still shows the right waits and countdowns. A wait turns
// amber and red at the moments the server gives (data-warn-at,
// data-limit-at), since those count only the team's business hours.
const AMBER = ["text-amber-600", "dark:text-amber-400", "font-medium"]
const RED = ["text-red-600", "dark:text-red-400", "font-medium"]

function duration(seconds) {
  const sign = seconds < 0 ? "-" : ""
  let rest = Math.abs(seconds)
  const hours = Math.floor(rest / 3600)
  rest -= hours * 3600
  const minutes = Math.floor(rest / 60)
  const secs = rest - minutes * 60
  const pad = (n) => String(n).padStart(2, "0")
  return sign + (hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${pad(minutes)}:${pad(secs)}`)
}

export default class extends Controller {
  static targets = ["since", "due", "updated", "zone"]

  connect() {
    this.tick()
    this.timer = setInterval(() => this.tick(), 1000)
    this.frame = this.element.closest("turbo-frame")
    this.onMissing = (event) => this.lostAccess(event)
    this.onRender = () => this.tick()
    this.frame?.addEventListener("turbo:frame-missing", this.onMissing)
    this.frame?.addEventListener("turbo:frame-render", this.onRender)
  }

  disconnect() {
    clearInterval(this.timer)
    this.frame?.removeEventListener("turbo:frame-missing", this.onMissing)
    this.frame?.removeEventListener("turbo:frame-render", this.onRender)
  }

  // The server's time now: its render time, plus the seconds elapsed here
  // since that render arrived. A refresh morphs in a new render time, which
  // restarts the count.
  serverNow() {
    const localNow = Date.now() / 1000
    const renderedAt = Number(this.updatedTarget.dataset.at)
    if (renderedAt !== this.renderedAt) {
      this.renderedAt = renderedAt
      this.renderedLocal = localNow
    }
    return this.renderedAt + (localNow - this.renderedLocal)
  }

  tick() {
    const now = this.serverNow()
    this.sinceTargets.forEach((el) => {
      el.textContent = duration(Math.trunc(now - Number(el.dataset.at)))
      const limitAt = el.dataset.limitAt ? Number(el.dataset.limitAt) : null
      const warnAt = el.dataset.warnAt ? Number(el.dataset.warnAt) : null
      this.paint(el, limitAt !== null && now >= limitAt ? RED : warnAt !== null && now >= warnAt ? AMBER : null)
    })
    // A team's zone is named only when the viewer's own zone is at a
    // different offset at that moment.
    this.zoneTargets.forEach((el) => {
      const viewerOffset = -new Date(Number(el.dataset.at) * 1000).getTimezoneOffset() * 60
      el.hidden = viewerOffset === Number(el.dataset.offset)
    })
    this.dueTargets.forEach((el) => {
      const seconds = Math.trunc(Number(el.dataset.at) - now)
      el.textContent = duration(seconds)
      this.paint(el, seconds < 0 ? RED : AMBER)
    })
    this.updatedTargets.forEach((el) => {
      const ago = Math.max(0, Math.round(now - Number(el.dataset.at)))
      el.textContent = ago < 5 ? "Updated just now" : `Updated ${ago}s ago`
    })
  }

  paint(el, classes) {
    el.classList.remove(...AMBER, ...RED)
    if (classes) el.classList.add(...classes)
  }

  // A refresh comes back without this frame when the session ended (the
  // sign-in page) or when the viewer may no longer see the board (a 403).
  // Dropping src stops the frame from loading that page again.
  lostAccess(event) {
    event.preventDefault()
    this.frame.removeAttribute("src")
    clearInterval(this.timer)
    this.element.replaceChildren()
    const notice = document.createElement("p")
    notice.className = "text-sm text-[var(--pu-text-muted)]"
    notice.textContent = event.detail.response?.status === 403
      ? "You no longer have access to this board."
      : "Signed out. Refresh to sign in again."
    this.element.appendChild(notice)
  }
}
